import * as React from 'react';
import {getThemeMetadata} from '@sail/engine';
import type {Intent} from '@sail/engine';
import * as sailThemes from '../../themes';
import {messenger} from './messenger';

const EMPTY: Intent[] = [];

// The governed themes exported by `@sail/ui/themes`, paired with their metadata
// name so the toolkit can resolve a selection by name.
const GOVERNED_THEMES: {name: string; intent: Intent}[] = Object.values(
  sailThemes,
)
  .map((theme) => {
    const intent = theme as Intent;
    const meta = getThemeMetadata(intent);
    return meta ? {name: meta.name, intent} : null;
  })
  .filter((entry): entry is {name: string; intent: Intent} => entry != null);

/**
 * Subscribes to `SAIL_THEME_OVERRIDE` messages from the Sail Toolkit extension
 * and returns the `Intent`s for the selected themes.
 *
 * The extension sends the full set of selected theme names (by their metadata
 * name); we resolve each against `@sail/ui/themes`' exports. Multiple themes
 * compose in selection order.
 *
 * @param active - Whether the Sail Toolkit extension is active.
 * @returns The selected themes' `Intent`s, in selection order (empty when none).
 * @internal
 */
export function useThemeOverride(active: boolean): Intent[] {
  const [themeNames, setThemeNames] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!active) {
      return;
    }
    const unsubscribe = messenger?.onMessageType(
      'SAIL_THEME_OVERRIDE',
      ({themeNames: names}) => {
        setThemeNames(names);
      },
    );
    return () => unsubscribe?.();
  }, [active]);

  return React.useMemo(() => {
    if (themeNames.length === 0) {
      return EMPTY;
    }
    return themeNames
      .map((name) => GOVERNED_THEMES.find((t) => t.name === name)?.intent)
      .filter((intent): intent is Intent => intent != null);
  }, [themeNames]);
}
